import { Injectable, Inject } from '@angular/core';
import { authSelectors } from '@content-platform/web-angular-shared-util-auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { catchError, filter, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { roleActions, userPermissionListActions, userRoleListActions } from '../actions';
import { UserRoleListResource, UserPermissionListResource } from '../services';
import { DIRECT_ACCESS_DEFAULT_ROLE, OKTA_AUTH_DEFAULT_ACCESS_APPS, DIRECT_ACCESS_APP_RELATED_ROLES } from '../constants';
import { APPLICATION_API_OPTIONS } from './../application-api-config';
import { intersection } from 'lodash';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/effects";
import * as i2 from "../services";
import * as i3 from "@ngrx/store";
/**
 * The user role list effects
 *
 */
export class UserRoleListEffects {
    constructor(userRoleListActions$, api, permission_api, store, options) {
        this.userRoleListActions$ = userRoleListActions$;
        this.api = api;
        this.permission_api = permission_api;
        this.store = store;
        /**
         * Loads the list of roles for a given user and triggers a LoadSuccess action.
         *
         * @returns { Observable<Action> } Observable with LoadSuccess action when all roles are retrieved
         */
        this.loadById$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.LOAD_BY_ID), map((action) => action.payload), mergeMap((id) => this.api.get(id).pipe(switchMap((userRoleList) => {
            if (!userRoleList.uuid) {
                // Create user profile, updating a new user profile doesn't work.
                return this.api.create({ ...userRoleList, uuid: id });
            }
            return of(userRoleList);
        }), map((userRoleList) => {
            if (this.oktaAuthDefaultAccess) {
                const roles = userRoleList?.roles.map((role) => role.name);
                if (intersection(DIRECT_ACCESS_APP_RELATED_ROLES[this.appName], roles).length === 0) {
                    userRoleList.roles = [...userRoleList.roles, DIRECT_ACCESS_DEFAULT_ROLE.roles[0]];
                }
            }
            return new userRoleListActions.LoadByIdSuccess(userRoleList);
        }), catchError((error) => {
            if (this.oktaAuthDefaultAccess) {
                return of(new userRoleListActions.LoadDefaultRolesForDirectAccess());
            }
            if (error.includes('404 Not Found')) {
                const emptyRoles = {
                    id: null,
                    uuid: id,
                    roles: []
                };
                return of(new userRoleListActions.LoadByIdSuccess(emptyRoles));
            }
            return of(new userRoleListActions.LoadFailed({
                error: error,
                message: 'Unable to load user roles'
            }));
        })))));
        /**
         * Updates the list of roles a user has and triggers a UpdateSuccess action.
         *
         * @returns { Observable<Action> } Observable with UpdateSuccess action with the updated role
         */
        this.update$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.UPDATE), map((action) => action.payload), withLatestFrom(this.store.pipe(select(authSelectors.getUserId))), switchMap(([userRoleList, userId]) => {
            userRoleList.lastModifiedBy = userId;
            return this.api.update(userRoleList).pipe(map((updatedUserRoleList) => new userRoleListActions.UpdateSuccess(updatedUserRoleList)), catchError((error) => of(new userRoleListActions.LoadFailed({
                error: error,
                message: 'Unable to update user role'
            }))));
        })));
        /**
         * Forces Update of roles when a user role list is updated
         *
         */
        this.reloadRoles$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.UPDATE_SUCCESS), map((action) => action.payload), filter((userRoleList) => !!userRoleList.uuid), switchMap(() => [new roleActions.Load()])));
        /**
         * Forces Update of user permissions when a user role list is updated
         *
         */
        this.reloadPermissionsForUser$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.UPDATE_SUCCESS), map((action) => action.payload), filter((userRoleList) => !!userRoleList.uuid), mergeMap((userRoleList) => this.permission_api.get(userRoleList.uuid).pipe(map((userPermissionList) => new userPermissionListActions.UserRoleUpdateLoadByIdSuccess({
            ...userPermissionList,
            applications: userPermissionList.applications
        })))), catchError((error) => of(new userPermissionListActions.LoadFailed({
            error: error,
            message: 'Unable to load user permissions'
        })))));
        /**
         * Forces Update of user permissions when a user role list is updated for the logged in user
         *
         */
        this.reloadPermissions$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.UPDATE_SUCCESS), map((action) => action.payload), filter((userRoleList) => !!userRoleList.uuid), withLatestFrom(this.store.pipe(select(authSelectors.getADUser))), filter(([userRoleList, user]) => userRoleList.uuid === user.profile.oid), switchMap(([userRoleList]) => [new userPermissionListActions.LoadById(userRoleList.uuid)]), catchError((error) => of(new userPermissionListActions.LoadFailed({
            error: error,
            message: 'Unable to load user permissions'
        })))));
        this.setDefaultRole$ = createEffect(() => this.userRoleListActions$.pipe(ofType(userRoleListActions.LOAD_DEFAULT_ROLES_FOR_DIRECT_ACCESS), withLatestFrom(this.store.select(authSelectors.getUserId)), switchMap(([_, userId]) => [
            new userRoleListActions.LoadByIdSuccess({ ...DIRECT_ACCESS_DEFAULT_ROLE, id: 9999, uuid: userId })
        ])));
        this.oktaAuthDefaultAccess = OKTA_AUTH_DEFAULT_ACCESS_APPS.includes(options.appName);
        this.appName = options.appName;
    }
    static { this.ɵfac = function UserRoleListEffects_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || UserRoleListEffects)(i0.ɵɵinject(i1.Actions), i0.ɵɵinject(i2.UserRoleListResource), i0.ɵɵinject(i2.UserPermissionListResource), i0.ɵɵinject(i3.Store), i0.ɵɵinject(APPLICATION_API_OPTIONS)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UserRoleListEffects, factory: UserRoleListEffects.ɵfac }); }
}
//# sourceMappingURL=user-role-list.effects.js.map